import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, Users, BarChart3, Mic2, Shield, Wrench, FileText, Film, History,
} from 'lucide-react';
import ForecastSection from '../components/finance/ForecastSection';
import HistoryChart from '../components/finance/HistoryChart';
import ProjectHistoryModal from '../components/finance/ProjectHistoryModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const CATEGORY_META = {
  dublagem: { label: 'Dublagem', Icon: Mic2, bar: 'bg-teal-500',    text: 'text-teal-300' },
  gerencia: { label: 'Gerência', Icon: Shield, bar: 'bg-purple-500', text: 'text-purple-300' },
  mixagem:  { label: 'Mixagem',  Icon: Wrench, bar: 'bg-blue-500',   text: 'text-blue-300' },
  roteiro:  { label: 'Roteiro',  Icon: FileText, bar: 'bg-amber-500', text: 'text-amber-300' },
  srt:      { label: 'SRT',      Icon: Film, bar: 'bg-pink-500',    text: 'text-pink-300' },
};

const money = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;

const FinanceDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.role === 'dono';

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    axios
      .get(`${API}/finance-dashboard?year=${year}&month=${month}`, { headers: getAuthHeaders() })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month, isAdmin, getAuthHeaders]);

  const maxCategory = useMemo(() => {
    if (!data?.categories) return 0;
    return Math.max(1, ...Object.values(data.categories));
  }, [data]);

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="finance-dashboard-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-teal-400" />
              Dashboard Financeiro
            </h1>
            <p className="text-slate-400 mt-1">Visão gerencial dos pagamentos — {MONTHS[month - 1]} / {year}</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap" data-testid="finance-filter">
            <button
              onClick={() => setShowHistory(true)}
              className="flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="finance-history-btn"
            >
              <History className="h-4 w-4" /> Histórico
            </button>
            <select
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
              className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-teal-500"
              data-testid="finance-month-select"
            >
              {MONTHS.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
            </select>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-teal-500"
              data-testid="finance-year-select"
            >
              {[2023, 2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-teal-400" />
          </div>
        ) : !data ? (
          <p className="text-slate-400 text-center py-16">Erro ao carregar os dados.</p>
        ) : (
          <>
            {/* Totais */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                label="Total Pago"
                value={money(data.total_pago)}
                Icon={DollarSign}
                iconColor="text-emerald-400"
                testid="card-total-pago"
              />
              <MetricCard
                label="A Pagar"
                value={money(data.total_a_pagar)}
                Icon={DollarSign}
                iconColor="text-amber-400"
                testid="card-total-a-pagar"
              />
              <MetricCard
                label="Total do Mês"
                value={money(data.total_geral)}
                subValue={`${data.record_count} registro${data.record_count !== 1 ? 's' : ''}`}
                Icon={BarChart3}
                iconColor="text-teal-400"
                testid="card-total-geral"
              />
              <MetricCard
                label="vs. Mês Anterior"
                value={data.delta_pct === null ? '—' : `${data.delta_pct > 0 ? '+' : ''}${data.delta_pct}%`}
                subValue={money(data.prev_month_total)}
                Icon={data.delta_pct !== null && data.delta_pct >= 0 ? TrendingUp : TrendingDown}
                iconColor={data.delta_pct === null ? 'text-slate-400' : data.delta_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}
                testid="card-delta"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Categoria breakdown */}
              <section className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="finance-categories">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-teal-400" />
                  Gasto por Categoria
                </h2>
                {data.total_geral === 0 ? (
                  <p className="text-slate-400 text-sm">Nenhum pagamento registrado neste mês.</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(data.categories).map(([key, val]) => {
                      const meta = CATEGORY_META[key];
                      const pct = (val / maxCategory) * 100;
                      return (
                        <div key={key} data-testid={`category-${key}`}>
                          <div className="flex justify-between items-center mb-1 text-sm">
                            <span className={`flex items-center gap-2 ${meta.text} font-medium`}>
                              <meta.Icon className="h-4 w-4" />
                              {meta.label}
                            </span>
                            <span className="text-white font-mono">{money(val)}</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-slate-700 overflow-hidden">
                            <div
                              className={`h-full ${meta.bar} transition-all duration-500`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Top 5 */}
              <section className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="finance-top-people">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-400" />
                  Top 5 — Maiores Pagamentos
                </h2>
                {data.top_people.length === 0 ? (
                  <p className="text-slate-400 text-sm">Nenhum pagamento registrado neste mês.</p>
                ) : (
                  <ol className="space-y-2">
                    {data.top_people.map((p, idx) => (
                      <li
                        key={p.employee_id}
                        className="flex items-center justify-between bg-slate-900/40 border border-slate-700/60 rounded-lg px-3 py-2"
                        data-testid={`top-person-${idx}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            idx === 1 ? 'bg-slate-400/20 text-slate-300' :
                            idx === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-700 text-slate-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="text-white truncate">{p.employee_name || p.employee_id}</p>
                            <p className="text-xs text-slate-500">{p.count} registro{p.count !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <span className="font-mono text-emerald-400 shrink-0">{money(p.total)}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </div>
          </>
        )}

        {/* Previsão de Gastos Futuros */}
        <ForecastSection />

        {/* Histórico dos últimos 12 meses */}
        <HistoryChart months={12} />
      </div>
      <ProjectHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        canToggle={isAdmin}
      />
    </Layout>
  );
};

const MetricCard = ({ label, value, subValue, Icon, iconColor, testid }) => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
      <Icon className={`h-5 w-5 ${iconColor}`} />
    </div>
    <p className="text-2xl font-bold text-white font-mono">{value}</p>
    {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
  </div>
);

export default FinanceDashboard;
